import { createStore } from "redux";

import { MetaNode } from "../supportNodes";
import { PageNode, DynamoNode, ElementNode, InvalidNode, ElementType, LabelNode } from "dynamo-models";

export type StateMonad = (state: IEditorState) => IEditorState;
export type StateGuard = (state: IEditorState) => boolean;
export type GuardedStateMonad = (state: IEditorState) => IEditorState | undefined;
export type StateNomad = { do: GuardedStateMonad, undo: GuardedStateMonad }

export const guardThunk = (guard: StateGuard, stateMonad: StateMonad): GuardedStateMonad => (state: IEditorState) => {
    if (!guard(state)) {
        return undefined;
    }

    return stateMonad(state);
}

class MaxStack<TItem> {
    constructor(maxLength: number) {
        this.maxLength = maxLength;
    }

    private readonly maxLength: number;

    private _items: TItem[] = [];

    get items() {
        return this._items;
    }

    hasItems = () => this._items.length > 0;

    push = (item: TItem): boolean => {
        if (this.maxLength === 0) {
            return false;
        }

        if (this.maxLength === 1) {
            this._items = [item];
        }
        else if (this.items.length >= this.maxLength) {
            this._items = [item, ...this._items.slice(0, this.maxLength - 2)];
        }
        else {
            this._items = [item, ...this._items];
        }

        return true;
    }

    pop = (): TItem | undefined => this._items.shift();
}

export class HistoryMediator {
    constructor(maxUndo: number = 100, maxRedo: number = 100) {
        this.undoStack = maxUndo > 0 ? new MaxStack(maxUndo) : undefined;
        this.redoStack = maxRedo > 0 ? new MaxStack(maxRedo) : undefined;
    }

    private readonly undoStack?: MaxStack<StateNomad>;
    private readonly redoStack?: MaxStack<StateNomad>;

    private readonly canStep = (stack?: MaxStack<StateNomad>) => stack !== undefined && stack.hasItems();

    private readonly doStep = (state: IEditorState, sourceStack: MaxStack<StateNomad>, runner: (state: IEditorState, nomad: StateNomad) => IEditorState, targetStack?: MaxStack<StateNomad>): IEditorState => {
        if (!this.canStep(sourceStack)) {
            return state;
        }

        const nextThunk = sourceStack.pop();

        if (nextThunk === undefined) {
            return state;
        }

        const resultMaybe = runner(state, nextThunk);
        if (resultMaybe === undefined) {
            return this.doStep(state, sourceStack, runner, targetStack);
        }

        if (targetStack !== undefined) {
            targetStack.push(nextThunk);
        }
        
        return resultMaybe;
    }

    private getValueOrDefault<TVal>(val: TVal | undefined, fallback: TVal) {
        if (val === undefined) {
            return fallback;
        }

        return val;
    }

    apply = (nomad: StateNomad, state: IEditorState): IEditorState => {
        const resultMaybe = nomad.do(state);

        if (resultMaybe == undefined) {
            return state;
        }

        if (this.undoStack === undefined) {
            return resultMaybe;
        }

        this.undoStack.push(nomad);
        return resultMaybe;
    }


    canStepForward = () => this.canStep(this.redoStack);

    canStepBack = () => this.canStep(this.undoStack);

    stepForward = (state: IEditorState) => this.redoStack !== undefined ? this.doStep(state, this.redoStack, (state, thunk) => this.getValueOrDefault(thunk.do(state), state), this.undoStack) : state;

    stepBack = (state: IEditorState) => this.undoStack !== undefined ? this.doStep(state, this.undoStack, (state, thunk) => this.getValueOrDefault(thunk.undo(state), state), this.redoStack) : state;

    hasChanges = (): boolean => this.undoStack !== undefined && this.undoStack.hasItems();
}

export enum ToolboxTabs {
    Items = 0,
    Atttributes = 1,
    Styling = 2,
}

export interface IEditorState {
    mode: EditorMode;
    spacing: number;
    container: MetaNode;
    selectedNode?: DynamoNode;
    toolboxTab: ToolboxTabs;
    isUploadingImage: boolean;

    history: HistoryMediator;
}

export interface IEditorAction {
    type: string;
}

export interface IEditorReducer {
    (state: IEditorState, action: IEditorAction & any): IEditorState;
}

export enum EditorMode {
    Edit,
    Preview
}

const mutateEditorState = (state: IEditorState, forwardMonad: GuardedStateMonad, reverseMonad: GuardedStateMonad): IEditorState => {
    const stateNomad: StateNomad = { do: forwardMonad, undo: reverseMonad }
    
    return state.history.apply(stateNomad, state);
}

const SET_MODE = "SET_MODE";
const SET_SPACING = "SET_SPACING";
const DO_UNDO = "DO_UNDO";
const DO_REDO = "DO_REDO";
const ADD_PAGE = "ADD_PAGE";
const TRASH_NODE = "TRASH_NODE";
const TRASH_SELECTED_NODE = "TRASH_SELECTED_NODE";
const ARRANGE_NODE = "ARRANGE_NODE";
const SET_ATTRIBUTE = "SET_ATTRIBUTE";
const SET_TOOLBOX_TAB = "SET_TOOLBOX_TAB";
const SET_SELECTED_NODE = "SET_SELECTED_NODE";
const OPEN_IMAGE_UPLOAD = "OPEN_IMAGE_UPLOAD";
const CLOSE_IMAGE_UPLOAD = "CLOSE_IMAGE_UPLOAD";

const getSpoofState = (state: IEditorState) => ({ 
    ...state, 
    container: new MetaNode(state.container.formNode, state.container.toolboxContainer)
});

const getModeDo = (mode) => guardThunk(() => true, (state) => ({ ...state, mode: mode }));
const getModeUndo = (mode, currMode) => guardThunk((state) => state.mode === mode, (state) => ({ ...state, mode: currMode }));
const mutateMode = (state: IEditorState, mode: EditorMode) => mutateEditorState(state, getModeDo(mode), getModeUndo(mode, state.mode));

const getSpacingDo = (spacing) => guardThunk((state) => true, (state) => ({ ...state, spacing: spacing }));
const getSpacingUndo = (spacing, currSpacing) => guardThunk((state) => state.spacing === spacing, (state) => ({ ...state, spacing: currSpacing }));
const mutateSpacing = (state: IEditorState, spacing: number) => mutateEditorState(state, getSpacingDo(spacing), getSpacingUndo(spacing, state.spacing));

const getAppendPageDo = (page: PageNode) => guardThunk((state: IEditorState) => state.container.tryFindNode(page.nodeId) === undefined, (state: IEditorState) => {
    const newState = getSpoofState(state);
    newState.container.formNode.tryAddChild(page);
    return newState;
});
const getAppendPageUndo = (page: PageNode) => guardThunk((state: IEditorState) => state.container.formNode.tryFindNode(page.nodeId) !== undefined, (state: IEditorState) => {
    const newState = getSpoofState(state);
    newState.container.formNode.tryRemoveChild(page);
    return newState;
})
const appendPage = (state: IEditorState) => ((newPage: PageNode) => mutateEditorState(state, getAppendPageDo(newPage), getAppendPageUndo(newPage)))(new PageNode())

const getRemoveNodeDo = (node: DynamoNode, parentNode?: DynamoNode) => guardThunk((state: IEditorState) => state.container.formNode.tryFindNode(node.nodeId) !== undefined, (state: IEditorState) => {
    const newState = getSpoofState(state);
    if (parentNode !== undefined) {
        parentNode.tryRemoveChild(node);
    }
    return newState;
});
const getRemoveNodeUndo = (node: DynamoNode, parentNode?: DynamoNode) => ((index: number) => guardThunk((state: IEditorState) => parentNode !== undefined && state.container.formNode.tryFindNode(parentNode.nodeId) !== undefined, (state: IEditorState) => {
    const newState = getSpoofState(state);
    if (parentNode !== undefined) {
        parentNode.tryAddChild(node, index);
    }
    return newState;
}))(parentNode === undefined ? 0 : parentNode.getChildren().indexOf(node));
const removeNode = (state: IEditorState, node: DynamoNode) => ((parentNode?: DynamoNode) => mutateEditorState(state, getRemoveNodeDo(node, parentNode), getRemoveNodeUndo(node, parentNode)))(state.container.formNode.tryFindParentOf(node.nodeId))

const getArrangeNodeDo = (node: DynamoNode, parentNode: DynamoNode, index: number) => guardThunk(() => true, (state: IEditorState) => {
    const newState = getSpoofState(state);
    let oldParentMaybe = state.container.tryFindParentOf(node.nodeId);
    let sessionPropertyTagMaybe: string | undefined;
    let nodeTextMaybe: string | undefined;

    let elementNodeMaybe: ElementNode | undefined;
    let elementParentNodeMaybe: ElementNode | undefined;

    let textNodeMaybe: LabelNode | undefined;
    let parentTextNodeMaybe: LabelNode | undefined;

    const tryRemove = () => {
        if (oldParentMaybe !== undefined) {
            oldParentMaybe.tryRemoveChild(node);
        }
    };

    if (node.nodeId === parentNode.nodeId) {
        return newState;
    }

    if (node.tag === "Element") {
        elementNodeMaybe = node as ElementNode;
    }

    if (elementNodeMaybe !== undefined && elementNodeMaybe.elementType === ElementType.VariableText) {
        textNodeMaybe = elementNodeMaybe as LabelNode;
    }

    if (textNodeMaybe !== undefined && textNodeMaybe.dynamicName !== undefined && textNodeMaybe.dynamicName !== "") {
        sessionPropertyTagMaybe = `\$\{${textNodeMaybe.dynamicName}\}`;
    }

    if (textNodeMaybe !== undefined && textNodeMaybe.text !== undefined && textNodeMaybe.text !== "") {
        nodeTextMaybe = textNodeMaybe.text;
    }

    if (parentNode.tag === "Element") {
        elementParentNodeMaybe = parentNode as ElementNode;
    }

    if (elementParentNodeMaybe !== undefined && elementParentNodeMaybe.elementType === ElementType.VariableText) {
        parentTextNodeMaybe = elementParentNodeMaybe as LabelNode;
    }

    // The dropped node has a defined session property, doesn't have defined text, and the target node is a text node
    if (
        sessionPropertyTagMaybe !== undefined &&
        nodeTextMaybe === undefined &&
        parentTextNodeMaybe !== undefined
    ) {
        parentTextNodeMaybe.text = parentTextNodeMaybe.text === undefined ? sessionPropertyTagMaybe : `${parentTextNodeMaybe.text} ${sessionPropertyTagMaybe}`;
        tryRemove();
        return newState;
    }
    // The dropped node has defined text and the target node is a text node
    else if (
        nodeTextMaybe !== undefined &&
        parentTextNodeMaybe !== undefined
    ) {
        parentTextNodeMaybe.text = parentTextNodeMaybe.text === undefined ? nodeTextMaybe : `${parentTextNodeMaybe.text} ${nodeTextMaybe}`;
        tryRemove();
        return newState;
    }

    const didSwap = parentNode.tryReorderChild(node, index);
    if (didSwap) {
        oldParentMaybe = undefined;
    }

    if (oldParentMaybe !== undefined) {
        oldParentMaybe.tryRemoveChild(node);
    }
    
    if (!didSwap) {
        parentNode.tryAddChild(node, index);
    }
    
    return newState;
});
const getArrangeNodeUndo = (node: DynamoNode, pastParentNode?: DynamoNode, pastIndex?: number) => guardThunk((state: IEditorState) => state.container.formNode.tryFindNode(node.nodeId) !== undefined, (state: IEditorState) => {
    const newState = getSpoofState(state);
    const currParentMaybe = state.container.tryFindParentOf(node.nodeId);
    const didRemove = (
        currParentMaybe !== undefined &&
        currParentMaybe.tryRemoveChild(node)
    );

    if ((didRemove || pastParentNode === undefined) && pastParentNode !== undefined) {
        pastParentNode.tryAddChild(node, pastIndex)
    }

    return newState;
});
const arrangeNode = (state: IEditorState, node: DynamoNode, parentNode: DynamoNode, index: number) => (
    (currParent?: DynamoNode) => (
        (currIndex?: number) => mutateEditorState(state, getArrangeNodeDo(node, parentNode, index), getArrangeNodeUndo(node, currParent, currIndex)))(currParent === undefined ? undefined : currParent.getIndexOf(node)))(state.container.tryFindParentOf(node.nodeId));

const getSetAttribute = (setThunk: () => void) => guardThunk(_ => true, (state: IEditorState) => {
    const newState = getSpoofState(state);
    setThunk();
    return newState;
});
const setAttribute = (state: IEditorState, setThunk: () => void, unsetThunk: () => void) => mutateEditorState(state, getSetAttribute(setThunk), getSetAttribute(unsetThunk));

const setToolboxTab = (state: IEditorState, newTab: ToolboxTabs) => ({ ...state, toolboxTab: newTab});

const setNode = (state: IEditorState, node: DynamoNode) => ({ ...state, toolboxTab: ToolboxTabs.Atttributes, selectedNode: node });

const openUpload = (state: IEditorState) => {
    const newState = getSpoofState(state);
    newState.isUploadingImage = true;
    return newState;
};

const closeUpload = (state: IEditorState) => {
    const newState = getSpoofState(state);
    newState.isUploadingImage = false;
    return newState;
}

export const setMode = (mode: EditorMode) => ({
    type: SET_MODE,
    mode
})

export const setSpacing = (spacing: number) => ({
    type: SET_SPACING,
    spacing
})

export const doUndo = () => ({
    type: DO_UNDO
})

export const doRedo = () => ({
    type: DO_REDO
})

export const addPage = () => ({
    type: ADD_PAGE
});

export const trashNode = (node: DynamoNode) => ({
    type: TRASH_NODE,
    node: node
});

export const trashSelectedNode = () => ({
    type: TRASH_SELECTED_NODE,
});

export const doArrangeNode = (childNode: DynamoNode, parentNode: DynamoNode, index?: number) => ({
    type: ARRANGE_NODE,
    childNode: childNode,
    parentNode: parentNode,
    index: index
});

export const doSetAttribute = (setThunk: () => void, unsetThunk: () => void) => ({
    type: SET_ATTRIBUTE,
    setThunk: setThunk,
    unsetThunk: unsetThunk
})

export const doSetToolboxTab = (tab: ToolboxTabs) => ({
    type: SET_TOOLBOX_TAB,
    tab: tab
})

export const doSetSelectedNode = node => ({
    type: SET_SELECTED_NODE,
    node: node
});

export const openImageUpload = () => ({
    type: OPEN_IMAGE_UPLOAD,
});

export const closeImageUpload = () => ({
    type: CLOSE_IMAGE_UPLOAD,
});

export const editorReducer = (initState: IEditorState) => (state = initState, action): IEditorState => {
    switch (action.type) {
        case SET_ATTRIBUTE:
        return setAttribute(state, action.setThunk, action.unsetThunk);

        case SET_MODE:
        return mutateMode(state, action.mode);

        case SET_SPACING:
        return mutateSpacing(state, action.spacing);

        case ADD_PAGE:
        return appendPage(state);

        case TRASH_NODE:
        return removeNode(state, action.node);

        case TRASH_SELECTED_NODE:
        return removeNode(state, state.selectedNode !== undefined ? state.selectedNode : new InvalidNode());

        case ARRANGE_NODE:
        return arrangeNode(state, action.childNode, action.parentNode, action.index);

        case SET_TOOLBOX_TAB:
        return setToolboxTab(state, action.tab);

        case SET_SELECTED_NODE:
        return setNode(state, action.node);

        case DO_UNDO:
        return state.history.stepBack(state);

        case DO_REDO:
        return state.history.stepForward(state);

        case OPEN_IMAGE_UPLOAD:
        return openUpload(state);

        case CLOSE_IMAGE_UPLOAD:
        return closeUpload(state);

        default:
        return state;
    }
}

export const createEditorStore = (initState: IEditorState) => createStore(editorReducer(initState));
